import { FunctionComponent } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Paper,
} from "@mui/material";

import { Cell, HeadCell, GrayRow } from "../styles";
import {
  formatFuelWeightCell,
  fuelWeightForCalc,
  MassDisplayUnit,
} from "../../utils/fuelFormat";

type FuelTableProps = Fuel &
  Weight & {
    weightUnit?: MassDisplayUnit | string;
    fuelRows?: FuelRow[] | null;
  };

// IMPORTANT: aggregate fuel weights (PLNTOF, MINBLKF, BLKF) are pre-computed
// in the backend in lb and converted+rounded once when the operator's display
// unit is kg. Re-computing them on the client from already-rounded inputs
// causes drift (e.g. 6195 + 249 + 200 = 6644 vs DB-pre-computed 6645). We
// therefore prefer the DB value and fall back to the local sum only when
// the DB column is missing.
const preferDbOrCalc = (
  dbValue: number | string | null | undefined,
  fallback: number,
): number => {
  if (dbValue === null || dbValue === undefined || dbValue === "") {
    return fallback;
  }
  const numeric = Number(dbValue);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const FuelTable: FunctionComponent<FuelTableProps> = ({
  trip_fuel_weight,
  trip_fuel_duration,
  altn,
  MINTOF_weight,
  MINTOF_duration,
  FRF_weight,
  FRF_duration,
  add_fuel_weight,
  add_fuel_duration,
  actual_onblock_fuel,
  indicated_offblock_fuel,
  contingency_fuel_weight,
  contingency_fuel_duration,
  planners_take_off_fuel_weight,
  planners_take_off_fuel_duration,
  taxi_fuel_weight,
  taxi_fuel_duration,
  MINBLK_weight,
  MINBLK_duration,
  extra_fuel_weight,
  extra_fuel_duration,
  DISCFUEL_weight,
  APUFUEL_weight,
  BLK_weight,
  tankeringReason,
  ZFM,
  TOM,
  LM,
  weightUnit,
  fuelRows,
}) => {
  const tripFuelWeight = formatFuelWeightCell(trip_fuel_weight, weightUnit);
  const MINTOFWeight = formatFuelWeightCell(MINTOF_weight, weightUnit);
  const FRFWeight = formatFuelWeightCell(FRF_weight, weightUnit);
  const addFuelWeight = formatFuelWeightCell(add_fuel_weight, weightUnit);
  const contingencyFuelWeight = formatFuelWeightCell(contingency_fuel_weight, weightUnit);
  const taxiFuelWeight = formatFuelWeightCell(taxi_fuel_weight, weightUnit);
  const extraFuelWeight = formatFuelWeightCell(extra_fuel_weight, weightUnit);
  const DISCFUELWeight = formatFuelWeightCell(DISCFUEL_weight, weightUnit);
  const APUFUELWeight = formatFuelWeightCell(APUFUEL_weight, weightUnit);
  const actualOnblockFuel = formatFuelWeightCell(actual_onblock_fuel, weightUnit);
  const indicatedOffblockFuel = formatFuelWeightCell(indicated_offblock_fuel, weightUnit);

  const calcMintof = fuelWeightForCalc(MINTOFWeight, weightUnit);
  const calcFrf = fuelWeightForCalc(FRFWeight, weightUnit);
  const calcTrip = fuelWeightForCalc(tripFuelWeight, weightUnit);
  const calcCont = fuelWeightForCalc(contingencyFuelWeight, weightUnit);
  const calcTaxi = fuelWeightForCalc(taxiFuelWeight, weightUnit);
  const calcExtra = fuelWeightForCalc(extraFuelWeight, weightUnit);
  const calcDisc = fuelWeightForCalc(DISCFUELWeight, weightUnit);

  const PLNTOF_weight = formatFuelWeightCell(
    preferDbOrCalc(planners_take_off_fuel_weight, calcMintof + calcCont),
    weightUnit,
  );
  const calcPlntof = fuelWeightForCalc(PLNTOF_weight, weightUnit);

  const MINBLK_weight_display = formatFuelWeightCell(
    preferDbOrCalc(MINBLK_weight, calcPlntof + calcTaxi),
    weightUnit,
  );
  const calcMinblk = fuelWeightForCalc(MINBLK_weight_display, weightUnit);

  const BLK_weight_display = formatFuelWeightCell(
    preferDbOrCalc(BLK_weight, calcMinblk + calcExtra + calcDisc),
    weightUnit,
  );

  const PLNTOF_duration =
    Number(MINTOF_duration ?? 0) + Number(contingency_fuel_duration ?? 0);
  const MINBLK_duration_display = MINBLK_duration ?? PLNTOF_duration + Number(taxi_fuel_duration ?? 0);

  const useSchema = Array.isArray(fuelRows) && fuelRows.length > 0;
  // Field rows that historically rendered with the emphasized (gray) style.
  const GRAY_FIELD_IDS = new Set(["block", "indicated"]);

  const schemaWeightById = (id: string): number | null => {
    const row = fuelRows?.find((entry) => entry.id === id);
    return row?.weight ?? null;
  };

  const renderSchemaBody = () => {
    // Highlight the ALTN that matches the schema alternate aggregate when
    // present. Do not hardcode mintof/trip/frf — those ids may be missing
    // after a custom reorder.
    const alternateRowWeight = schemaWeightById("alternate");
    const alternateAggregate =
      alternateRowWeight === null || alternateRowWeight === undefined
        ? null
        : fuelWeightForCalc(alternateRowWeight, weightUnit);

    return fuelRows!.filter((row) => row.id !== "onblock").map((row) => {
      if (row.isAlternateGroup) {
        return altn.map((altnFuel: AltnFuel) => {
          const altnWeight = fuelWeightForCalc(altnFuel.weight, weightUnit);
          const isPrimary =
            alternateAggregate !== null && altnWeight === alternateAggregate;
          return (
          <TableRow key={`altn-${altnFuel.airport_icao}-${altnFuel.sequence}`}>
            <Cell component="th" scope="row">
              {isPrimary ? (
                <b>
                  {row.label}
                  {altnFuel.sequence}: {altnFuel.airport_icao}
                </b>
              ) : (
                <>
                  {row.label}
                  {altnFuel.sequence}: {altnFuel.airport_icao}
                </>
              )}
            </Cell>
            <Cell>{formatFuelWeightCell(altnFuel.weight, weightUnit)}</Cell>
            <Cell>{altnFuel.duration}</Cell>
          </TableRow>
          );
        });
      }

      // Preserve the historical behavior of hiding the indicated block fuel
      // row when there is no value (null/undefined/""), but still show 0.
      const rawRowWeight = row.weight as number | string | null | undefined;
      if (
        row.id === "indicated" &&
        (rawRowWeight === null || rawRowWeight === undefined || rawRowWeight === "")
      ) {
        return null;
      }

      const RowComponent = row.isSum || GRAY_FIELD_IDS.has(row.id) ? GrayRow : TableRow;
      const durationCell =
        row.id === "discretionary"
          ? tankeringReason || ""
          : row.duration === null || row.duration === undefined
          ? ""
          : row.duration;

      return (
        <RowComponent key={`${row.type}-${row.id}`}>
          <Cell component="th" scope="row">
            {row.label}:
          </Cell>
          <Cell>{formatFuelWeightCell(row.weight, weightUnit)}</Cell>
          <Cell>{durationCell}</Cell>
        </RowComponent>
      );
    });
  };

  return (
    <TableContainer component={Paper}>
      <Table aria-label="fuel table">
        <TableHead>
          <TableRow>
            <HeadCell>
              <b>Fuel</b>
            </HeadCell>
            <HeadCell>Weight</HeadCell>
            <HeadCell>Duration</HeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {useSchema && renderSchemaBody()}
          {useSchema && (
            <TableRow>
              <Cell component="th" scope="row">
                ONBLKF:
              </Cell>
              <Cell>{actualOnblockFuel}</Cell>
              <Cell />
            </TableRow>
          )}
          {!useSchema && (
          <>
          <TableRow>
            <Cell component="th" scope="row">
              TRIP:
            </Cell>
            <Cell>{tripFuelWeight}</Cell>
            <Cell>{trip_fuel_duration}</Cell>
          </TableRow>

          {altn.map((altnFuel: AltnFuel) => (
            <TableRow key={altnFuel.airport_icao}>
              <Cell component="th" scope="row">
                {calcMintof - calcTrip - calcFrf == fuelWeightForCalc(altnFuel.weight, weightUnit) ?
                <b>ALTN{altnFuel.sequence}: {altnFuel.airport_icao}</b> :
                <>ALTN{altnFuel.sequence}: {altnFuel.airport_icao}</>}
              </Cell>
              <Cell>{formatFuelWeightCell(altnFuel.weight, weightUnit)}</Cell>
              <Cell>{altnFuel.duration}</Cell>
            </TableRow>
          ))}

          <TableRow>
            <Cell component="th" scope="row">
              FRF:
            </Cell>
            <Cell>{FRFWeight}</Cell>
            <Cell>{FRF_duration}</Cell>
          </TableRow>

          <TableRow>
            <Cell component="th" scope="row">
              ADDF:
            </Cell>
            <Cell>{addFuelWeight}</Cell>
            <Cell>{add_fuel_duration}</Cell>
          </TableRow>

          <GrayRow>
            <Cell component="th" scope="row">
              MINTOF:
            </Cell>
            <Cell>{MINTOFWeight}</Cell>
            <Cell>{MINTOF_duration}</Cell>
          </GrayRow>

          <TableRow>
            <Cell component="th" scope="row">
              CONT:
            </Cell>
            <Cell>{contingencyFuelWeight}</Cell>
            <Cell>{contingency_fuel_duration}</Cell>
          </TableRow>

          <GrayRow>
            <Cell component="th" scope="row">
              PLNTOF:
            </Cell>
            <Cell>{PLNTOF_weight}</Cell>
            <Cell>{planners_take_off_fuel_duration}</Cell>
          </GrayRow>

          <TableRow>
            <Cell component="th" scope="row">
              TAXI:
            </Cell>
            <Cell>{taxiFuelWeight}</Cell>
            <Cell>{taxi_fuel_duration}</Cell>
          </TableRow>

          <GrayRow>
            <Cell component="th" scope="row">
              MINBLKF:
            </Cell>
            <Cell>{MINBLK_weight_display}</Cell>
            <Cell>{MINBLK_duration_display}</Cell>
          </GrayRow>

          <TableRow>
            <Cell component="th" scope="row">
              EXTRA:
            </Cell>
            <Cell>{extraFuelWeight}</Cell>
            <Cell>{extra_fuel_duration}</Cell>
          </TableRow>

          <TableRow>
            <Cell component="th" scope="row">
              DISCF:
            </Cell>
            <Cell>{DISCFUELWeight}</Cell>
            <Cell>{tankeringReason || ""}</Cell>
          </TableRow>

          <TableRow>
            <Cell component="th" scope="row">
              APU:
            </Cell>
            <Cell>{APUFUELWeight}</Cell>
            <Cell />
          </TableRow>

          <GrayRow>
            <Cell component="th" scope="row">
              BLKF:
            </Cell>
            <Cell>{BLK_weight_display}</Cell>
            <Cell />
          </GrayRow>

          {indicated_offblock_fuel && <GrayRow>
            <Cell component="th" scope="row">
              Indicated BLKF:
            </Cell>
            <Cell>{indicatedOffblockFuel}</Cell>
            <Cell />
          </GrayRow>}

          <TableRow>
            <Cell component="th" scope="row">
              ONBLKF:
            </Cell>
            <Cell>{actualOnblockFuel}</Cell>
            <Cell />
          </TableRow>
          </>
          )}

          <TableRow>
            <HeadCell>ZFW: {ZFM}</HeadCell>
            <HeadCell>TOW: {TOM}</HeadCell>
            <HeadCell>LW: {LM}</HeadCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
};
